import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["abRulesSelect", "dayOfWeek", "timeOfDay"]

  connect() {
    this.toggleAbRuleFields();
    this.abRulesSelectTarget.addEventListener("change", () => {
      this.toggleAbRuleFields();
    });

    this.timeOfDayTarget.addEventListener("change", (event) => {
      if (this.abRulesSelectTarget.value === "time" && event.target.type === "time") {
        this.validateTimeChange(event.target);
        setTimeout(() => this.checkTimeOverlaps(), 100);
      }
    });

    this.timeOfDayTarget.addEventListener("input", (event) => {
      if (this.abRulesSelectTarget.value === "time" && event.target.type === "time") {
        this.validateTimeChange(event.target);
        setTimeout(() => this.checkTimeOverlaps(), 100);
      }
    });
  }

  toggleAbRuleFields() {
    const abRulesSelectValue = this.abRulesSelectTarget.value;
    if (abRulesSelectValue === "day") {
      this.getAbRulesText().textContent = "Select which days each experience will be served.";
      this.dayOfWeekTarget.classList.remove("hidden");
      this.timeOfDayTarget.classList.add("hidden");
      setTimeout(() => this.checkDayConflicts(), 100);
    } else if (abRulesSelectValue === "time") {
      this.getAbRulesText().textContent = "Select what time of the day experiences will served.";
      this.dayOfWeekTarget.classList.add("hidden");
      this.timeOfDayTarget.classList.remove("hidden");
      setTimeout(() => this.checkTimeOverlaps(), 100);
    } else {
      this.getAbRulesText().textContent = "Experiences will be served at random.";
      this.dayOfWeekTarget.classList.add("hidden");
      this.timeOfDayTarget.classList.add("hidden");
    }
  }

  checkDayConflicts() {
    const dayCheckboxes = Array.from(document.querySelectorAll('input.day-checkbox:checked'))
      .filter(cb => this.isInVisibleOverlappingCard(cb));
    const dayGroups = {};

    dayCheckboxes.forEach(checkbox => {
      const dayValue = checkbox.value;
      const checkboxName = checkbox.name;
      const experienceMatch = checkboxName.match(/\[(\d+)\]/);
      
      if (!experienceMatch) return;
      
      const experienceIndex = experienceMatch[1];
      
      if (!dayGroups[dayValue]) {
        dayGroups[dayValue] = [];
      }
      
      dayGroups[dayValue].push(experienceIndex);
    });

    const conflicts = [];
    Object.keys(dayGroups).forEach(dayValue => {
      if (dayGroups[dayValue].length > 1) {
        conflicts.push(dayValue);
      }
    });

    if (conflicts.length > 0) {
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const conflictDays = conflicts.map(d => dayNames[d] || `Day ${d}`).join(', ');
      
      this.showConflictWarning(conflictDays);
    }
  }

  isInVisibleOverlappingCard(element) {
    const card = element.closest('[data-experience-index]');
    if (!card) return true;
    return card.style.display !== 'none';
  }

  showConflictWarning(conflictDays) {
    const message = `The following days are selected for multiple experiences: ${conflictDays}. Each day can only be assigned to one experience.`

    const event = new CustomEvent('flash:warning', {
      detail: { message }
    })
    document.dispatchEvent(event)
  }

  checkTimeOverlaps() {
    if (this.abRulesSelectTarget.value !== "time") return;

    const timeInputs = Array.from(this.timeOfDayTarget.querySelectorAll('input[type="time"][name*="[start_time]"], input[type="time"][name*="[end_time]"]'))
      .filter(input => this.isInVisibleOverlappingCard(input));
    const experiences = {};

    timeInputs.forEach(input => {
      const name = input.name;
      const experienceMatch = name.match(/\[(\d+)\]/);
      if (!experienceMatch) return;

      const experienceIndex = experienceMatch[1];
      const isStartTime = name.includes('[start_time]');
      const isEndTime = name.includes('[end_time]');

      if (!experiences[experienceIndex]) {
        experiences[experienceIndex] = { start: null, end: null };
      }

      if (isStartTime && input.value) {
        experiences[experienceIndex].start = input.value;
      } else if (isEndTime && input.value) {
        experiences[experienceIndex].end = input.value;
      }
    });

    const experienceIndices = Object.keys(experiences);
    const overlaps = [];

    for (let i = 0; i < experienceIndices.length; i++) {
      const exp1Index = experienceIndices[i];
      const exp1 = experiences[exp1Index];

      if (!exp1.start || !exp1.end) continue;

      for (let j = i + 1; j < experienceIndices.length; j++) {
        const exp2Index = experienceIndices[j];
        const exp2 = experiences[exp2Index];

        if (!exp2.start || !exp2.end) continue;

        if (this.timeRangesOverlap(exp1.start, exp1.end, exp2.start, exp2.end)) {
          overlaps.push({ exp1: exp1Index, exp2: exp2Index });
        }
      }
    }

    if (overlaps.length > 0) {
      this.showTimeOverlapWarning(overlaps);
    }
  }

  timeRangesOverlap(start1, end1, start2, end2) {
    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const parts = timeStr.split(':').map(Number);
      return (parts[0] || 0) * 60 + (parts[1] || 0);
    };

    const start1Min = timeToMinutes(start1);
    const end1Min = timeToMinutes(end1);
    const start2Min = timeToMinutes(start2);
    const end2Min = timeToMinutes(end2);

    // Ranges overlap when they share any moment (inclusive boundaries)
    return start1Min <= end2Min && start2Min <= end1Min;
  }

  validateTimeChange(changedInput) {
    if (this.abRulesSelectTarget.value !== "time") return;
    if (!this.isInVisibleOverlappingCard(changedInput)) return;

    const name = changedInput.name;
    const experienceMatch = name.match(/\[(\d+)\]/);
    if (!experienceMatch) return;

    const currentExperienceIndex = experienceMatch[1];

    let currentExpStart = null;
    let currentExpEnd = null;

    const allTimeInputs = Array.from(this.timeOfDayTarget.querySelectorAll('input[type="time"]'))
      .filter(input => this.isInVisibleOverlappingCard(input));
    allTimeInputs.forEach(input => {
      const inputName = input.name;
      const inputExpMatch = inputName.match(/\[(\d+)\]/);
      if (!inputExpMatch || inputExpMatch[1] !== currentExperienceIndex) return;

      if (inputName.includes('[start_time]')) {
        currentExpStart = input === changedInput ? changedInput.value : input.value;
      } else if (inputName.includes('[end_time]')) {
        currentExpEnd = input === changedInput ? changedInput.value : input.value;
      }
    });

    if (!currentExpStart || !currentExpEnd) return;

    const otherExperiences = {};
    allTimeInputs.forEach(input => {
      const inputName = input.name;
      const inputExpMatch = inputName.match(/\[(\d+)\]/);
      if (!inputExpMatch) return;

      const expIndex = inputExpMatch[1];
      if (expIndex === currentExperienceIndex) return;

      if (!otherExperiences[expIndex]) {
        otherExperiences[expIndex] = { start: null, end: null };
      }

      if (inputName.includes('[start_time]')) {
        otherExperiences[expIndex].start = input.value;
      } else if (inputName.includes('[end_time]')) {
        otherExperiences[expIndex].end = input.value;
      }
    });

    for (const expIndex in otherExperiences) {
      const otherExp = otherExperiences[expIndex];
      if (!otherExp.start || !otherExp.end) continue;

      if (this.timeRangesOverlap(currentExpStart, currentExpEnd, otherExp.start, otherExp.end)) {
        changedInput.value = '';
        
        const message = `This time range overlaps with another experience. Each time range must be unique and non-overlapping.`;
        const event = new CustomEvent('flash:warning', {
          detail: { message }
        });
        document.dispatchEvent(event);
        return;
      }
    }
  }

  showTimeOverlapWarning(overlaps) {
    const message = `Time ranges overlap between multiple experiences. Each time range must be unique and non-overlapping.`

    const event = new CustomEvent('flash:warning', {
      detail: { message }
    })
    document.dispatchEvent(event)
  }

  getAbRulesText() {
    return this.element.querySelector('#abRulesText');
  }
}
